// src/components/common/A4DocumentLayout/A4DocumentLayout.jsx
// Shared on-screen A4 print layout for offline sales documents — banner, 2-col info cards,
// optional items table, optional summary (notes + totals). Extracted from OfflineOrderA4Invoice /
// QuotationA4Document / ReturnNoteA4Document / DeliveryNoteA4Document / CreditNoteA4Document,
// which all shared this exact section skeleton and differed only in field bindings. Accent color
// follows the business's active theme (--color-primary), same as the rest of the app. Mirrors the
// same extraction already done for the PDF side in
// src/utils/offlineOrderPdfExporter.js (drawBanner/drawTwoColCards/drawItemsTable/drawTotalsAndNotes).
import React from 'react';
import styles from './A4DocumentLayout.module.css';

const renderLine = (line, i) => {
  if (typeof line !== 'object' || line === null || React.isValidElement(line)) {
    return <div key={i} className={styles.custDetail}>{line}</div>;
  }
  const { label, value, bold } = line;
  return (
    <div key={i} className={styles.custDetail}>
      <span>{label}: </span>{bold ? <strong>{value}</strong> : value}
    </div>
  );
};

const A4DocumentLayout = ({
  title,
  subtitle,
  logoUrl,
  documentNumber,
  metaRows = [],
  infoCards = [],
  items,
  summary,
  footerText,
  footerNote,
}) => (
  <div className={styles.a4Container}>
    <div className={styles.headerBanner}>
      <div className={styles.titleGroup}>
        {logoUrl && <img src={logoUrl} alt="" className={styles.shopLogo} />}
        <div>
          <h1 className={styles.companyTitle}>{title}</h1>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      </div>
      <div className={styles.invoiceMeta}>
        <h2 className={styles.invoiceNumber}>{documentNumber}</h2>
        {metaRows.map((row, i) => (
          <div key={i} className={styles.metaRow}>
            <span>{row.label}: </span><strong>{row.value}</strong>
          </div>
        ))}
      </div>
    </div>

    {infoCards.length > 0 && (
      <div className={styles.infoGrid}>
        {infoCards.map((card, i) => (
          <div key={i} className={styles.infoCard}>
            <div className={styles.cardTitle}>{card.title}</div>
            {card.primary && <div className={styles.custName}>{card.primary}</div>}
            {(card.lines || []).map(renderLine)}
          </div>
        ))}
      </div>
    )}

    {items && (
      <table className={styles.table}>
        <thead>
          <tr>
            {items.columns.map((col, i) => (
              <th
                key={i}
                className={col.align === 'right' ? styles.textRight : col.align === 'center' ? styles.textCenter : undefined}
                style={col.width ? { width: col.width } : undefined}
              >
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.rows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) => (
                <td
                  key={ci}
                  className={items.columns[ci]?.align === 'right' ? styles.textRight : items.columns[ci]?.align === 'center' ? styles.textCenter : undefined}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    )}

    {summary?.totals ? (
      <div className={styles.summarySection}>
        {summary.notes && (
          <div className={styles.notesBox}>
            <strong>{summary.notes.label}:</strong>
            <p style={{ margin: '4px 0 0 0' }}>{summary.notes.text}</p>
          </div>
        )}
        <div className={styles.totalsBox}>
          {summary.totals.map((row, i) => (
            <div key={i} className={row.variant === 'grand' ? styles.grandTotalRow : styles.totalRow} style={row.color ? { color: row.color, fontWeight: 600 } : undefined}>
              <span>{row.label}</span>
              <span>{row.value}</span>
            </div>
          ))}
        </div>
      </div>
    ) : summary?.notes && (
      <div className={styles.notesBox}>
        <strong>{summary.notes.label}:</strong>
        <p style={{ margin: '4px 0 0 0' }}>{summary.notes.text}</p>
      </div>
    )}

    <div className={styles.footer}>
      {footerText && <p>{footerText}</p>}
      <p style={{ margin: footerText ? '2px 0 0 0' : 0 }}>{footerNote}</p>
    </div>
  </div>
);

export default A4DocumentLayout;
